/**
 * ตรรกะตรวจสิทธิ์แบบ pure function (ไม่ผูกกับ React state)
 *
 * P-S616-1: เดิมตรรกะนี้อยู่ใน AuthContext อย่างเดียว → โค้ดที่ต้องตัดสินใจ "ทันทีหลัง login"
 * (ก่อน context state จะ re-render) ใช้ไม่ได้ ต้องอ่าน permissions จาก localStorage เอง
 * แล้วเขียนตรรกะซ้ำ = เสี่ยง drift. แยกออกมาเป็นฟังก์ชันบริสุทธิ์ให้ทั้ง AuthContext และ
 * LoginForm เรียกใช้ชุดเดียวกัน
 */

import type { UserPermissions } from '../types/permission';

/**
 * ตรวจ permission key เดียว รองรับทั้ง flat key (`permissions['rentals.view']`)
 * และ nested object (`permissions.rentals.view`)
 */
export const hasPermissionIn = (
  permissions: UserPermissions,
  permissionKey: string
): boolean => {
  // flat key เช่น permissions["dashboard.view"] = true
  if (permissions[permissionKey] === true) {
    return true;
  }

  // nested key เช่น permissions.dashboard.view = true
  let current: any = permissions;
  for (const key of permissionKey.split('.')) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return false;
    }
  }

  return current === true;
};

/**
 * สร้าง callback สำหรับเช็คสิทธิ์เมนู (มีอย่างน้อย 1 permission ในลิสต์ = เข้าถึงได้)
 * ใช้ร่วมกับ `resolveLandingRoute` ใน config/navigation.tsx
 *
 * @param permissions - permissions object ของผู้ใช้
 * @param isSuperAdmin - super admin/super user ผ่านทุกเมนู (ตรงกับ bypass ฝั่ง backend)
 *   G-02: bypass model เดียวกันทั้ง 4 ชั้น (ProtectedRoute, AuthContext.hasPermission,
 *   AuthGuard, buildMenuAccessChecker ตัวนี้) — backend core/permissions.py bypass บน is_superuser
 */
export const buildMenuAccessChecker = (
  permissions: UserPermissions,
  isSuperAdmin: boolean
) => (required?: string[]): boolean => {
  // เมนูที่ไม่ระบุสิทธิ์ = เปิดให้ทุกคน (ตรงกับ hasMenuPermission ใน Layout)
  if (!required || required.length === 0) return true;
  if (isSuperAdmin) return true;
  return required.some(p => hasPermissionIn(permissions, p));
};
